import { Link } from "react-router-dom";
import { Phone, MapPin } from "lucide-react";
import { CONTACT_PLACEHOLDERS, NAV_LINKS, OM_SERVICE, SERVICES } from "@/data/content";

const LOGO_CLEAR = "/brand/evovlex-logo-transparent.png";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="border-t border-slate-800/80 bg-navy-950 pb-10 pt-20 text-slate-400 grain relative">
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 md:grid-cols-2 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-4">
          <Link to="/" data-testid="footer-logo-link" aria-label="EVOVLEX IT — Home" className="inline-flex">
            <span className="inline-flex rounded-lg bg-white px-3 py-2">
              <img src={LOGO_CLEAR} alt="EVOVLEX IT" className="h-9 w-auto" />
            </span>
          </Link>
          <p className="mt-5 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-cyan-400">
            Technology | Security | Innovation
          </p>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">
            EVOVLEX IT delivers innovative technology solutions designed to help businesses transform, secure, and
            scale in an increasingly digital world.
          </p>
        </div>

        <div className="lg:col-span-2">
          <h3 className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">Quick Links</h3>
          <ul className="mt-5 space-y-3" data-testid="footer-quick-links">
            {NAV_LINKS.map((l) => (
              <li key={l.label}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-sm transition-colors hover:text-cyan-400"
                >
                  {l.label === "Why EVOVLEX" ? "Why EVOVLEX IT" : l.label === "About" ? "About Us" : l.label === "Contact" ? "Contact Us" : l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h3 className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">Services</h3>
          <ul className="mt-5 space-y-3" data-testid="footer-services-links">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`footer-service-${s.slug}`}
                  className="text-sm transition-colors hover:text-cyan-400"
                >
                  {s.title}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to={`/services/${OM_SERVICE.slug}`}
                data-testid="footer-service-om-manpower"
                className="text-sm font-medium text-orange-300/90 transition-colors hover:text-orange-300"
              >
                {OM_SERVICE.title}
              </Link>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h3 className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">Contact</h3>
          <ul className="mt-5 space-y-4 text-sm" data-testid="footer-contact-info">
            <li className="flex items-start gap-3">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-cyan-500" />
              <a
                href="tel:+917706355112"
                data-testid="footer-phone-link"
                className="text-slate-300 transition-colors hover:text-cyan-400"
              >
                {CONTACT_PLACEHOLDERS.phone}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-cyan-500" />
              <span className="text-slate-400" data-testid="footer-address">
                Office No. 321, 3rd Floor, Siddhi Nisarga Plaza,
                <br />
                Bhumkar Wasti, Hinjewadi Road, Pune.
              </span>
            </li>
          </ul>
          <Link
            to="/contact"
            data-testid="footer-cta-contact"
            className="mt-6 inline-flex items-center gap-2 rounded-lg border border-slate-700 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-cyan-500/60 hover:text-cyan-300"
          >
            Talk to an Expert
          </Link>
        </div>
      </div>

      <div className="mx-auto mt-16 flex w-full max-w-7xl flex-col items-start justify-between gap-3 border-t border-slate-800/80 px-4 pt-8 sm:px-6 md:flex-row md:items-center lg:px-12">
        <p className="text-xs text-slate-500">© {year} EVOVLEX IT. All rights reserved.</p>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-600">
          Empowering Businesses Through Technology
        </p>
      </div>
    </footer>
  );
}
